import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  ArrowLeft, Star, Eye, EyeOff, ArrowUp, ArrowDown, Save, Search,
  Image as ImageIcon, Check, AlertCircle, Trash2, FolderX,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (url) => url?.startsWith('http') ? url : `${API}${url}`;
const MAX_VISIBLE = 10;

const AdminPhotoPicker = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState([]);
  const [activeSlug, setActiveSlug] = useState(null);
  const [editImages, setEditImages] = useState([]);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/auth/me`, { credentials: 'include' })
      .then(r => { if (!r.ok) navigate('/admin/login'); })
      .catch(() => navigate('/admin/login'));
  }, [navigate]);

  const load = () => {
    setLoading(true);
    fetch(`${API}/api/fleet-media/all?include_hidden=true`)
      .then(r => r.json())
      .then(d => {
        setVehicles(d.vehicles || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(load, []);

  const filtered = useMemo(() => {
    if (!search) return vehicles;
    const s = search.toLowerCase();
    return vehicles.filter(v =>
      (v.vehicle_name || '').toLowerCase().includes(s) ||
      (v.vehicle_slug || '').toLowerCase().includes(s) ||
      (v.category || '').toLowerCase().includes(s)
    );
  }, [vehicles, search]);

  const openEditor = (vehicle) => {
    // Build editable copies sorted by existing display_order (or default index)
    const imgs = (vehicle.images || [])
      .map((img, idx) => ({
        ...img,
        hidden: !!img.hidden,
        display_order: typeof img.display_order === 'number' ? img.display_order : idx,
      }))
      .sort((a, b) => a.display_order - b.display_order);
    setActiveSlug(vehicle.vehicle_slug || vehicle.vehicle_id);
    setEditImages(imgs);
    setDirty(false);
  };

  const closeEditor = () => {
    if (dirty && !window.confirm('You have unsaved changes. Discard?')) return;
    setActiveSlug(null);
    setEditImages([]);
    setDirty(false);
  };

  const toggleHidden = (fid) => {
    setEditImages(prev => prev.map(i => i.file_id === fid ? { ...i, hidden: !i.hidden } : i));
    setDirty(true);
  };

  const move = (fid, delta) => {
    setEditImages(prev => {
      const idx = prev.findIndex(i => i.file_id === fid);
      if (idx < 0) return prev;
      const newIdx = idx + delta;
      if (newIdx < 0 || newIdx >= prev.length) return prev;
      const copy = [...prev];
      [copy[idx], copy[newIdx]] = [copy[newIdx], copy[idx]];
      return copy.map((img, i) => ({ ...img, display_order: i }));
    });
    setDirty(true);
  };

  const makeFeatured = (fid) => {
    setEditImages(prev => {
      const target = prev.find(i => i.file_id === fid);
      if (!target) return prev;
      const others = prev.filter(i => i.file_id !== fid);
      const reordered = [{ ...target, hidden: false }, ...others];
      return reordered.map((img, i) => ({ ...img, display_order: i }));
    });
    setDirty(true);
  };

  const deletePhoto = async (fid) => {
    if (!window.confirm('Delete this photo permanently? This cannot be undone.')) return;
    try {
      const r = await fetch(`${API}/api/fleet-media/${activeSlug}/photo/${fid}`, { method: 'DELETE' });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || 'Delete failed');
      }
      setEditImages(prev => prev.filter(i => i.file_id !== fid).map((img, i) => ({ ...img, display_order: i })));
      toast.success('Photo deleted');
      load();
    } catch (e) { toast.error(e.message); }
  };

  const deleteFolder = async (slug, name) => {
    if (!window.confirm(`Delete the entire "${name}" folder? All ${(vehicles.find(v => (v.vehicle_slug || v.vehicle_id) === slug)?.images || []).length} photos will be permanently removed. This cannot be undone.`)) return;
    try {
      const r = await fetch(`${API}/api/fleet-media/${slug}/folder`, { method: 'DELETE' });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || 'Delete failed');
      }
      toast.success(`Folder "${name}" deleted`);
      if (activeSlug === slug) { setActiveSlug(null); setEditImages([]); setDirty(false); }
      load();
    } catch (e) { toast.error(e.message); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = editImages.map((img, i) => ({
        file_id: img.file_id,
        hidden: !!img.hidden,
        display_order: i,
      }));
      const r = await fetch(`${API}/api/fleet-media/${activeSlug}/images-reorder`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ images: payload }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Save failed');
      }
      const result = await r.json();
      toast.success(`Saved — ${result.visible} visible, ${result.hidden} hidden`);
      setDirty(false);
      load();
    } catch (e) {
      toast.error(e.message);
    }
    setSaving(false);
  };

  const visibleCount = editImages.filter(i => !i.hidden).length;
  const overCap = visibleCount > MAX_VISIBLE;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-photo-picker">
      {/* Header */}
      <div className="bg-black text-white px-4 py-3 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin')} className="text-gray-400 hover:text-white"><ArrowLeft className="h-5 w-5" /></button>
            <div>
              <h1 className="text-lg font-bold flex items-center gap-2"><ImageIcon className="h-5 w-5 text-red-500" /> Photo Picker</h1>
              <p className="text-[11px] text-gray-400">Choose which photos show first · hide unwanted shots · reorder</p>
            </div>
          </div>
          <p className="text-[11px] text-gray-400">Showing up to first <span className="text-red-500 font-bold">{MAX_VISIBLE}</span> visible photos per vehicle on public site</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Search */}
        <div className="mb-4 relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search vehicles…" className="pl-9 h-10" data-testid="pp-search" />
        </div>

        {loading ? (
          <div className="text-center py-20 text-gray-500">Loading…</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map(v => {
              const total = (v.images || []).length;
              const visible = (v.images || []).filter(i => !i.hidden).length;
              const featured = (v.images || []).find(i => !i.hidden) || (v.images || [])[0];
              return (
                <Card key={v.vehicle_slug || v.vehicle_id} className="overflow-hidden flex flex-col" data-testid={`pp-card-${v.vehicle_slug || v.vehicle_id}`}>
                  {featured ? (
                    <div className="h-40 bg-gray-100 overflow-hidden">
                      <img src={resolveUrl(featured.url)} alt={v.vehicle_name} className="w-full h-full object-cover" />
                    </div>
                  ) : (
                    <div className="h-40 bg-gray-100 flex items-center justify-center text-gray-400 text-xs">No photos</div>
                  )}
                  <div className="p-4 flex-1 flex flex-col">
                    <p className="text-[9px] font-bold uppercase tracking-wider text-red-600">{v.category}</p>
                    <h3 className="text-sm font-bold text-black leading-tight mb-2 line-clamp-2">{v.vehicle_name}</h3>
                    <div className="flex items-center justify-between text-[11px] text-gray-500 mb-3">
                      <span>{visible} visible · {total - visible} hidden</span>
                      {visible > MAX_VISIBLE && <span className="text-amber-600 font-semibold flex items-center gap-1"><AlertCircle className="h-3 w-3" /> Over {MAX_VISIBLE}</span>}
                    </div>
                    <Button onClick={() => openEditor(v)} className="w-full h-8 text-xs bg-red-600 hover:bg-red-700" data-testid={`pp-edit-${v.vehicle_slug || v.vehicle_id}`}>
                      Edit Photos
                    </Button>
                    <button
                      onClick={() => deleteFolder(v.vehicle_slug || v.vehicle_id, v.vehicle_name || v.vehicle_slug)}
                      className="mt-2 text-[10px] uppercase tracking-wider text-gray-400 hover:text-red-600 inline-flex items-center gap-1 self-start"
                      data-testid={`pp-delete-folder-${v.vehicle_slug || v.vehicle_id}`}
                    >
                      <FolderX className="h-3 w-3" /> Delete entire folder
                    </button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Editor modal */}
      {activeSlug && (
        <div className="fixed inset-0 bg-black/70 z-40 flex items-center justify-center p-3" data-testid="pp-editor-modal">
          <div className="bg-white rounded-xl max-w-5xl w-full max-h-[92vh] flex flex-col overflow-hidden">
            <div className="bg-black text-white px-5 py-4 flex items-center justify-between sticky top-0">
              <div>
                <p className="text-[10px] text-red-500 uppercase tracking-widest font-bold">Editing</p>
                <h2 className="font-bold">{activeSlug}</h2>
              </div>
              <div className="flex items-center gap-2">
                <div className={`text-[11px] px-3 py-1 rounded-full ${overCap ? 'bg-amber-500/20 text-amber-400' : 'bg-green-500/20 text-green-400'}`}>
                  {visibleCount} visible {overCap && `/ only first ${MAX_VISIBLE} appear publicly`}
                </div>
                <Button onClick={closeEditor} variant="ghost" size="sm" className="text-white hover:bg-white/10 h-8">Close</Button>
                <Button onClick={save} disabled={saving || !dirty} className="bg-red-600 hover:bg-red-700 h-8 text-xs" data-testid="pp-save-btn">
                  <Save className="h-3.5 w-3.5 mr-1.5" /> {saving ? 'Saving…' : dirty ? 'Save Changes' : 'Saved'}
                </Button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {editImages.length === 0 ? (
                <div className="text-center py-10 text-gray-500">No photos in this group.</div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {editImages.map((img, i) => {
                    const isFeatured = !img.hidden && i === 0;
                    const willShow = !img.hidden && editImages.filter((x, xi) => !x.hidden && xi <= i).length <= MAX_VISIBLE;
                    return (
                      <div
                        key={img.file_id}
                        className={`border-2 rounded-lg overflow-hidden transition-all ${
                          img.hidden ? 'border-gray-200 bg-gray-50 opacity-60' :
                          isFeatured ? 'border-amber-400 shadow-md' :
                          'border-gray-200'
                        }`}
                        data-testid={`pp-photo-${img.file_id}`}
                      >
                        <div className="relative h-40 bg-gray-100">
                          <img src={resolveUrl(img.url)} alt="" className="w-full h-full object-cover" />
                          {isFeatured && (
                            <div className="absolute top-2 left-2 bg-amber-400 text-black text-[10px] font-bold px-2 py-1 rounded-full flex items-center gap-1">
                              <Star className="h-3 w-3 fill-black" /> FEATURED
                            </div>
                          )}
                          {!isFeatured && willShow && !img.hidden && (
                            <div className="absolute top-2 left-2 bg-black/70 text-white text-[10px] font-bold px-2 py-1 rounded-full">
                              #{i + 1}
                            </div>
                          )}
                          {img.hidden && (
                            <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                              <span className="text-white text-xs font-bold bg-black/70 px-3 py-1 rounded-full flex items-center gap-1">
                                <EyeOff className="h-3 w-3" /> HIDDEN
                              </span>
                            </div>
                          )}
                          {!img.hidden && !willShow && (
                            <div className="absolute inset-0 bg-black/20 flex items-center justify-center pointer-events-none">
                              <span className="text-white text-[10px] font-bold bg-amber-600 px-2 py-1 rounded-full">BEYOND FIRST {MAX_VISIBLE}</span>
                            </div>
                          )}
                        </div>
                        <div className="p-2 flex items-center justify-between bg-white">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => toggleHidden(img.file_id)}
                              title={img.hidden ? 'Show' : 'Hide'}
                              className={`h-7 w-7 rounded-md flex items-center justify-center text-xs ${img.hidden ? 'bg-gray-200 text-gray-500 hover:bg-gray-300' : 'bg-red-100 text-red-600 hover:bg-red-200'}`}
                              data-testid={`pp-toggle-${img.file_id}`}
                            >
                              {img.hidden ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                            </button>
                            {!img.hidden && !isFeatured && (
                              <button
                                onClick={() => makeFeatured(img.file_id)}
                                title="Set as featured (#1)"
                                className="h-7 w-7 rounded-md flex items-center justify-center text-amber-600 bg-amber-50 hover:bg-amber-100"
                                data-testid={`pp-star-${img.file_id}`}
                              >
                                <Star className="h-3.5 w-3.5" />
                              </button>
                            )}
                            <button
                              onClick={() => deletePhoto(img.file_id)}
                              title="Delete this photo permanently"
                              className="h-7 w-7 rounded-md flex items-center justify-center text-red-700 bg-red-50 hover:bg-red-100"
                              data-testid={`pp-delete-${img.file_id}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => move(img.file_id, -1)}
                              disabled={i === 0}
                              title="Move up"
                              className="h-7 w-7 rounded-md flex items-center justify-center bg-gray-100 hover:bg-gray-200 disabled:opacity-30"
                              data-testid={`pp-up-${img.file_id}`}
                            >
                              <ArrowUp className="h-3.5 w-3.5" />
                            </button>
                            <button
                              onClick={() => move(img.file_id, 1)}
                              disabled={i === editImages.length - 1}
                              title="Move down"
                              className="h-7 w-7 rounded-md flex items-center justify-center bg-gray-100 hover:bg-gray-200 disabled:opacity-30"
                              data-testid={`pp-down-${img.file_id}`}
                            >
                              <ArrowDown className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer help */}
            <div className="bg-gray-50 border-t px-5 py-3 text-[11px] text-gray-500 flex items-center gap-4 flex-wrap">
              <span className="flex items-center gap-1.5"><Star className="h-3 w-3 text-amber-500" /> Star = featured (first shown)</span>
              <span className="flex items-center gap-1.5"><EyeOff className="h-3 w-3 text-red-500" /> Hide from public</span>
              <span className="flex items-center gap-1.5"><Check className="h-3 w-3 text-green-500" /> Only first {MAX_VISIBLE} visible photos appear on Fleet Gallery / Rates</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPhotoPicker;
